import { useEffect, useRef, useState } from "react";
import { Canvas as FabricCanvas, Textbox, IText, FabricObject, FabricImage } from "fabric";
import { Button } from "@/components/ui/button";
import { Type, List, Trash2 } from "lucide-react";

interface Slide {
  id: string;
  index: number;
  title: string;
  bullets?: string[];
  paragraph?: string;
  notes: string;
  imageUrl?: string | null;
}

interface SlideTheme {
  backgroundColor: string;
  titleColor: string;
  textColor: string;
  accentColor: string;
  fontFamily: string;
  titleFontSize: number;
  textFontSize: number;
  bulletStyle: string;
  backgroundGradient?: string | null;
}

interface VisualSlideCanvasProps {
  slide: Slide;
  theme?: SlideTheme;
  onUpdate: (updates: Partial<Slide>) => void;
}

const VisualSlideCanvas = ({ slide, theme, onUpdate }: VisualSlideCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<FabricCanvas | null>(null);
  const [selectedObject, setSelectedObject] = useState<FabricObject | null>(null);

  // Default theme
  const defaultTheme: SlideTheme = {
    backgroundColor: "#ffffff",
    titleColor: "#1a1a1a",
    textColor: "#333333",
    accentColor: "#0066cc",
    fontFamily: "Arial, sans-serif",
    titleFontSize: 48,
    textFontSize: 24,
    bulletStyle: "•",
  };

  const activeTheme = theme || defaultTheme;

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = new FabricCanvas(canvasRef.current, {
      width: 960,
      height: 540,
      backgroundColor: activeTheme.backgroundColor,
    });

    fabricCanvasRef.current = canvas;

    // Selection events
    canvas.on("selection:created", (e) => {
      setSelectedObject(e.selected?.[0] || null);
    });

    canvas.on("selection:updated", (e) => {
      setSelectedObject(e.selected?.[0] || null);
    });

    canvas.on("selection:cleared", () => {
      setSelectedObject(null);
    });

    // Object modification events - update slide data
    canvas.on("object:modified", () => {
      syncCanvasToSlide(canvas);
    });

    // Load initial slide content
    loadSlideToCanvas(canvas, slide);

    return () => {
      canvas.dispose();
    };
  }, [slide.id]);

  // Reload canvas when slide or theme changes
  useEffect(() => {
    if (fabricCanvasRef.current) {
      console.log('Reloading canvas for slide:', slide.id, slide.title, 'with theme:', activeTheme);
      loadSlideToCanvas(fabricCanvasRef.current, slide);
    }
  }, [slide.id, slide.title, (slide.bullets?.length ?? (slide.paragraph ? 1 : 0)), slide.paragraph, slide.imageUrl, JSON.stringify(theme)]);

  const loadSlideToCanvas = (canvas: FabricCanvas, slideData: Slide) => {
    canvas.clear();
    
    // Apply background
    if (activeTheme.backgroundGradient) {
      canvas.backgroundColor = activeTheme.backgroundGradient;
    } else {
      canvas.backgroundColor = activeTheme.backgroundColor;
    }

    // Layout constants
    const CANVAS_WIDTH = 960;
    const CANVAS_HEIGHT = 540;
    const MARGIN = 40;
    const SPACING = 20;
    
    // Determine layout: with or without image
    const hasImage = !!slideData.imageUrl;
    const contentWidth = hasImage ? CANVAS_WIDTH * 0.5 - MARGIN * 2 : CANVAS_WIDTH - MARGIN * 2;
    const imageWidth = hasImage ? CANVAS_WIDTH * 0.4 : 0;
    const imageLeft = hasImage ? CANVAS_WIDTH - imageWidth - MARGIN : 0;

    // Add title - fixed position at top
    const title = new Textbox(slideData.title, {
      left: MARGIN,
      top: MARGIN,
      width: contentWidth,
      fontSize: activeTheme.titleFontSize,
      fontWeight: "bold",
      fontFamily: activeTheme.fontFamily,
      fill: activeTheme.titleColor,
      editable: true,
      splitByGrapheme: true,
    });
    title.set("slideElement", "title");
    canvas.add(title);

    // Get actual rendered height of title after it's added to canvas
    const actualTitleHeight = title.getScaledHeight();

    // Calculate available space for content with actual title height
    const bulletStartTop = MARGIN + actualTitleHeight + SPACING;
    const availableHeight = CANVAS_HEIGHT - bulletStartTop - MARGIN;
    const bullets = slideData.bullets || [];
    const paragraph = slideData.paragraph;
    const contentLines = paragraph ? [paragraph] : bullets;
    const lineCount = contentLines.length;
    
    // Dynamic spacing based on available space and count
    const maxLineHeight = activeTheme.textFontSize * 2.5;
    const lineSpacing = lineCount > 0 
      ? Math.min(maxLineHeight, availableHeight / lineCount)
      : maxLineHeight;

    // Add paragraph or bullets
    let contentTop = bulletStartTop;
    contentLines.forEach((line, idx) => {
      const prefix = paragraph ? "" : `${activeTheme.bulletStyle} `;
      const textContent = new Textbox(prefix + line, {
        left: MARGIN + (paragraph ? 0 : 30),
        top: contentTop,
        width: contentWidth - (paragraph ? 0 : 30),
        fontSize: activeTheme.textFontSize,
        fontFamily: activeTheme.fontFamily,
        fill: activeTheme.textColor,
        editable: true,
        splitByGrapheme: true,
      });
      textContent.set("slideElement", paragraph ? "paragraph" : "bullet");
      textContent.set("bulletIndex", idx);
      canvas.add(textContent);
      contentTop += lineSpacing;
    });

    // Add background image if exists - after text so it doesn't overlap
    if (slideData.imageUrl && hasImage) {
      FabricImage.fromURL(slideData.imageUrl, { crossOrigin: 'anonymous' }).then((img) => {
        if (!img) return;
        
        // Calculate image dimensions to fit in allocated space
        const maxImgWidth = imageWidth;
        const maxImgHeight = CANVAS_HEIGHT - MARGIN * 2;
        
        // Scale to fit while maintaining aspect ratio
        const scale = Math.min(
          maxImgWidth / (img.width || 1), 
          maxImgHeight / (img.height || 1)
        );
        img.scale(scale);
        
        // Center vertically in available space
        const scaledHeight = (img.height || 0) * scale;
        const verticalCenter = MARGIN + (maxImgHeight - scaledHeight) / 2;
        
        img.set({
          left: imageLeft,
          top: verticalCenter,
          selectable: true,
        });
        canvas.add(img);
        canvas.renderAll();
      }).catch((err) => {
        console.error('Failed to load slide image:', err);
      });
    }

    canvas.renderAll();
  };

  const syncCanvasToSlide = (canvas: FabricCanvas) => {
    const objects = canvas.getObjects();
    let newTitle = slide.title;
    const newBullets: string[] = [];
    let newParagraph: string | undefined;

    objects.forEach((obj) => {
      if (obj instanceof Textbox || obj instanceof IText) {
        const elementType = obj.get("slideElement");
        
        if (elementType === "title") {
          newTitle = obj.text || "";
        } else if (elementType === "paragraph") {
          newParagraph = obj.text || "";
        } else if (elementType === "bullet") {
          const bulletIndex = obj.get("bulletIndex") as number;
          let text = obj.text || "";
          text = text.replace(/^[•▸■→]\s*/, "");
          newBullets[bulletIndex] = text;
        }
      }
    });

    const updates: Partial<Slide> = { title: newTitle };
    if (newParagraph !== undefined) {
      updates.paragraph = newParagraph;
      updates.bullets = [];
    } else {
      updates.bullets = newBullets.filter(b => b !== undefined);
      updates.paragraph = undefined;
    }
    onUpdate(updates);
  };

  const addTextBox = () => {
    if (!fabricCanvasRef.current) return;

    const textbox = new Textbox("Click to edit", {
      left: 100,
      top: 200,
      width: 300,
      fontSize: activeTheme.textFontSize,
      fill: activeTheme.textColor,
      editable: true,
      fontFamily: activeTheme.fontFamily,
    });

    fabricCanvasRef.current.add(textbox);
    fabricCanvasRef.current.setActiveObject(textbox);
    fabricCanvasRef.current.renderAll();
  };

  const addBulletPoint = () => {
    if (!fabricCanvasRef.current) return;

    const bulletIdx = (slide.bullets || []).length;
    const bulletTop = 150 + bulletIdx * 70;

    const bulletText = new Textbox(`${activeTheme.bulletStyle} New bullet point`, {
      left: 80,
      top: bulletTop,
      width: 800,
      fontSize: activeTheme.textFontSize,
      fontFamily: activeTheme.fontFamily,
      fill: activeTheme.textColor,
      editable: true,
    });
    bulletText.set("slideElement", "bullet");
    bulletText.set("bulletIndex", bulletIdx);

    fabricCanvasRef.current.add(bulletText);
    fabricCanvasRef.current.renderAll();

    onUpdate({
      bullets: [...(slide.bullets || []), "New bullet point"],
      paragraph: undefined,
    });
  };

  const deleteSelected = () => {
    if (!fabricCanvasRef.current || !selectedObject) return;

    fabricCanvasRef.current.remove(selectedObject);
    fabricCanvasRef.current.renderAll();
    setSelectedObject(null);

    syncCanvasToSlide(fabricCanvasRef.current);
  };

  const handleTextChange = () => {
    if (fabricCanvasRef.current) {
      syncCanvasToSlide(fabricCanvasRef.current);
    }
  };

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    canvas.on("text:changed", handleTextChange);

    return () => {
      canvas.off("text:changed", handleTextChange);
    };
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button onClick={addTextBox} size="sm" variant="outline">
          <Type className="mr-2 h-4 w-4" />
          Add Text
        </Button>
        <Button onClick={addBulletPoint} size="sm" variant="outline">
          <List className="mr-2 h-4 w-4" />
          Add Bullet
        </Button>
        {selectedObject && (
          <Button onClick={deleteSelected} size="sm" variant="destructive">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        )}
      </div>

      <div
        className="border rounded-lg shadow-lg overflow-hidden p-4"
        style={{
          background: activeTheme.backgroundGradient || activeTheme.backgroundColor,
        }}
      >
        <div className="bg-transparent">
          <canvas ref={canvasRef} />
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        <p>💡 Click any text to edit directly. Drag to move. Resize using corner handles.</p>
      </div>
    </div>
  );
};

export default VisualSlideCanvas;
